import * as React from 'react';
import { useEffect } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import Chip from '@mui/material/Chip';
import User from 'benchmarking-domain/user';

import useApi from '../../../hooks/use-api';

interface UserSelectProps {
  id?: string;
  label?: string;
  organisationId?: string;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

export function UserSelect({
  id,
  label,
  organisationId,
  value,
  onChange,
  disabled,
}: UserSelectProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly User[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [fetchTriggered, setFetchTriggered] = React.useState(false);

  const { subscribeUsers } = useApi();

  useEffect(() => {
    if (!fetchTriggered || !organisationId) {
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeUsers(organisationId, (results: User[]) => {
      setOptions(results?.sort((one, two) => (one.email < two.email ? -1 : 1)));
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [fetchTriggered, organisationId, subscribeUsers]);

  const handleOpen = () => {
    setOpen(true);
    setFetchTriggered(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Autocomplete
      multiple
      id={id}
      options={options.filter((o) => !value.some((v) => v === o.email)).map((u) => u.email)}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      loading={loading}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.toLowerCase() === value.toLowerCase()}
      getOptionLabel={(option) => {
        const user = options.find((o) => o.email === option);

        if (user) {
          return user.email + (user.name ? ` (${user.name})` : '');
        }

        return option;
      }}
      freeSolo
      autoSelect
      value={value}
      onChange={(e, value) => {
        onChange(value.map((v: string) => v.toLowerCase()));
      }}
      renderTags={(value: string[], getTagProps) =>
        value.map((option: string, index: number) => (
          <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
        ))
      }
      renderInput={(params) => <TextField {...params} variant="outlined" label={label} />}
    />
  );
}
